import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";

interface Repository {
  id: number;
  name: string;
  full_name: string;
  description: string;
  private: boolean;
  owner: {
    login: string;
    type: string;
  };
  enableACR: boolean;
}

interface GitHubAccount {
  id: number;
  name: string;
  type: "User" | "Organization";
  avatarUrl: string;
  installationId: number;
}

interface ToggleProps {
  enabled: boolean;
  onChange: (enabled: boolean) => void;
  loading?: boolean;
}

const Toggle = ({ enabled, onChange, loading }: ToggleProps) => (
  <button
    type="button"
    onClick={() => !loading && onChange(!enabled)}
    disabled={loading}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
      enabled ? "bg-green-600" : "bg-gray-600"
    } ${loading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
  >
    <span
      className={`${
        enabled ? "translate-x-6" : "translate-x-1"
      } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
    />
    {loading && (
      <span className="absolute inset-0 flex items-center justify-center">
        <span className="animate-spin h-4 w-4 border-2 border-white rounded-full border-t-transparent" />
      </span>
    )}
  </button>
);

const GitHubSettings = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [selectedAccount, setSelectedAccount] = useState<string>("");
  const [accounts, setAccounts] = useState<GitHubAccount[]>([]);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [repositories, setRepositories] = useState<Repository[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [updatingRepo, setUpdatingRepo] = useState<number | null>(null);

  // Check initial GitHub connection status and load accounts
  useEffect(() => {
    const loadAccounts = async () => {
      try {
        // Try to list GitHub accounts - this will fail if not connected
        const response = await api.get<{ accounts: GitHubAccount[] }>(
          "/github/accounts",
        );
        setIsConnected(true);
        setAccounts(response.data.accounts);

        // If we have no selection and there are accounts, select the first one
        if (!selectedAccount && response.data.accounts.length > 0) {
          setSelectedAccount(response.data.accounts[0].name);
        }
      } catch (error) {
        const err = error as { response?: { status?: number } };
        if (err.response?.status === 401) {
          setIsConnected(false);
          setError("GitHub connection required");
        } else {
          console.error("Error checking GitHub connection:", error);
          setError("Failed to check GitHub connection status");
        }
      }
    };

    if (user) {
      loadAccounts();
    }
  }, [user, selectedAccount]);

  // Load repositories when account is selected
  useEffect(() => {
    const loadRepositories = async () => {
      if (!isConnected || !selectedAccount) return;

      try {
        setIsLoading(true);
        const selectedAccountData = accounts.find(
          (acc) => acc.name === selectedAccount,
        );
        const response = await api.get<{ repositories: Repository[] }>(
          "/github/repositories",
          {
            params: {
              type:
                selectedAccountData?.type === "Organization"
                  ? "organization"
                  : "personal",
              org:
                selectedAccountData?.type === "Organization"
                  ? selectedAccount
                  : undefined,
            },
          },
        );
        setRepositories(response.data.repositories);
      } catch (error) {
        console.error("Error loading repositories:", error);
        setError("Failed to load repositories");
      } finally {
        setIsLoading(false);
      }
    };

    loadRepositories();
  }, [isConnected, selectedAccount, accounts]);

  // Handle OAuth callback
  useEffect(() => {
    const code = searchParams.get("code");
    const error = searchParams.get("error");
    const state = searchParams.get("state");
    const installation_id = searchParams.get("installation_id");
    const setup_action = searchParams.get("setup_action");

    if (code && state) {
      handleOAuthCallback(code, state, installation_id, setup_action);
    } else if (error) {
      setError("Failed to connect GitHub account");
    }
  }, [searchParams]);

  const handleAddAccount = async () => {
    try {
      setIsConnecting(true);
      setError(null);
      // Start OAuth flow for adding new account
      const response = await api.get("/github/oauth/add-account");
      // Redirect to GitHub target selection page
      window.location.href = response.data.url;
    } catch (error) {
      console.error("Error adding GitHub account:", error);
      setError("Failed to start account addition process");
    } finally {
      setIsConnecting(false);
    }
  };

  const handleReconnect = async () => {
    try {
      setIsConnecting(true);
      setError(null);
      // Start regular OAuth flow for reconnecting
      const response = await api.get("/github/oauth/login");
      // Redirect to GitHub OAuth page
      window.location.href = response.data.url;
    } catch (error) {
      console.error("Error reconnecting GitHub:", error);
      setError("Failed to start GitHub reconnection");
    } finally {
      setIsConnecting(false);
    }
  };

  const handleOAuthCallback = async (
    code: string,
    state: string,
    installation_id?: string | null,
    setup_action?: string | null,
  ) => {
    try {
      setIsConnecting(true);
      const isAddingAccount = state.startsWith("add_account_");
      const endpoint = isAddingAccount
        ? "/github/oauth/add-account/callback"
        : "/github/oauth/callback";

      await api.get(endpoint, {
        params: {
          code,
          state,
          ...(installation_id && { installation_id }),
          ...(setup_action && { setup_action }),
        },
      });
      setIsConnected(true);
      setError(null);

      // Refresh accounts list after adding account
      const response = await api.get<{ accounts: GitHubAccount[] }>(
        "/github/accounts",
      );
      setAccounts(response.data.accounts);

      // Navigate to the settings page with success message
      window.location.href = "/app/github/settings?connected=true";
    } catch (error) {
      console.error("Error handling OAuth callback:", error);
      setError("Failed to connect GitHub account");
      window.location.href = "/app/github/settings?error=auth_failed";
    } finally {
      setIsConnecting(false);
    }
  };

  const handleToggleACR = async (repoId: number, enabled: boolean) => {
    try {
      setUpdatingRepo(repoId);

      const repo = repositories.find((r) => r.id === repoId);
      if (!repo) {
        throw new Error("Repository not found");
      }

      // Toggle ACR
      await api.post(`/github/repositories/${repoId.toString()}/acr`, {
        enabled,
      });

      // Update local state
      setRepositories((repos) =>
        repos.map((r) => (r.id === repoId ? { ...r, enableACR: enabled } : r)),
      );
    } catch (error) {
      console.error("Error toggling ACR:", error);
      setError("Failed to update repository settings");
    } finally {
      setUpdatingRepo(null);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 text-white">
      <h1 className="aurora-gradient">GitHub Settings</h1>

      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4 text-white/80">
          GitHub Account
        </h2>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <div className="relative">
              <select
                id="account-select"
                value={selectedAccount}
                onChange={(e) => setSelectedAccount(e.target.value)}
                className="select-aurora"
              >
                {accounts.map((account) => (
                  <option key={account.id} value={account.name}>
                    {account.name} ({account.type})
                  </option>
                ))}
              </select>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleAddAccount}
                disabled={isConnecting}
                className="button-secondary button-sm"
                aria-label="Add GitHub account"
              >
                + Add account
              </button>
              {isConnected && (
                <button
                  onClick={handleReconnect}
                  disabled={isConnecting}
                  className={`button-secondary button-sm ${
                    isConnecting ? "cursor-not-allowed" : ""
                  }`}
                >
                  {isConnecting ? "Connecting..." : "Reconnect"}
                </button>
              )}
            </div>
            {error && (
              <div className="mt-2 p-4 bg-red-50 text-red-700 rounded">
                {error}
              </div>
            )}
          </div>
        </div>
      </div>

      {isConnected && (
        <div>
          <h2 className="text-xl font-semibold mb-4 text-white/80">
            Repository Management
          </h2>
          <div className="shadow rounded-lg p-6">
            {isLoading ? (
              <p className="text-gray-200">Loading repositories...</p>
            ) : repositories.length > 0 ? (
              <div className="space-y-4">
                <p className="text-gray-200 mb-4">
                  Enable or disable AI Code Review for your repositories:
                </p>
                <div className="divide-y divide-gray-700">
                  {repositories.map((repo) => (
                    <div
                      key={repo.id}
                      className="py-4 flex items-center justify-between"
                    >
                      <div>
                        <h3 className="text-lg font-medium">
                          {repo.full_name}
                        </h3>
                        <p className="text-sm text-gray-400">
                          {repo.description}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-400">
                          {repo.enableACR ? "ACR Enabled" : "ACR Disabled"}
                        </span>
                        <Toggle
                          enabled={repo.enableACR}
                          onChange={() =>
                            handleToggleACR(repo.id, !repo.enableACR)
                          }
                          loading={updatingRepo === repo.id}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-gray-600">
                No repositories found for your account.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GitHubSettings;
